import { useLayoutEffect, useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { makeRoundedBox } from './roundedBoxGeometry'
import type { BoardLayout } from './useBoardLayout'
import type { BiomeTheme } from '@/styles/theme'

interface TilesProps {
  cells: string[]
  layout: BoardLayout
  theme: BiomeTheme
  reducedMotion: boolean
  graphics: 'high' | 'medium' | 'low'
}

interface SurfaceStyle {
  top: string
  body: string
  accent: string
  detail: string
  emissive: string
  roughness: number
  metalness: number
  glow: number
}

interface DeckSeamSegment {
  key: string
  x: number
  y: number
  scale: [number, number, number]
}

export type TileSurfaceDetailKind = 'forest-moss-root' | 'cave-slate-chip' | 'lava-basalt-fissure' | 'ice-snow-crust' | 'magical-rune-inlay' | 'dungeon-paver-chip'

export interface TileSurfaceDetailInfo {
  key: string
  x: number
  y: number
  tile: string
  kind: TileSurfaceDetailKind
  rot: number
  scale: number
  tone: number
  wear: number
  edgeWear: number
  patchCount: number
  patchSkew: number
  variant: number
}

export type TileEdgeReliefSide = 'north' | 'south' | 'east' | 'west'
export type TileEdgeReliefKind = 'forest-moss-lip' | 'cave-chipped-lip' | 'lava-basalt-lip' | 'ice-snow-lip' | 'magical-rune-lip' | 'dungeon-paver-lip'

export interface TileEdgeReliefInfo {
  key: string
  x: number
  y: number
  tile: string
  side: TileEdgeReliefSide
  kind: TileEdgeReliefKind
  boundary: boolean
  rot: number
  offset: number
  shift: number
  length: number
  depth: number
  height: number
  tone: number
  opacity: number
  variant: number
}

const FLOOR_H = 0.07
const CAP_H = 0.035
const WALL_H = 0.22
export const FLOOR_SLAB_SCALE = 1.075
export const CAP_SLAB_SCALE = 1.07
export const WALL_SLAB_SCALE = 0.99
const FLOOR_TILES = '-oepgkt'
const LOW_FLOOR_TILES = '-oepgktsl~wm'
const WALL_TILES = 'xbd'
const surfaceTextureCache = new Map<string, THREE.Texture>()

function hashCell(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function shifted(color: string, lightness: number): string {
  return new THREE.Color(color).offsetHSL(0, 0, lightness).getStyle()
}

function mixed(a: string, b: string, amount: number): string {
  return new THREE.Color(a).lerp(new THREE.Color(b), amount).getStyle()
}

export function biomeDeckSeamStyle(id: BiomeTheme['id']): { color: string; opacity: number; pulse: number } | null {
  if (id === 'lava') return { color: '#ff5a1f', opacity: 0.68, pulse: 0.2 }
  if (id === 'magical') return { color: '#ff73ff', opacity: 0.5, pulse: 0.18 }
  if (id === 'ice') return { color: '#91efff', opacity: 0.34, pulse: 0.1 }
  if (id === 'forest') return { color: '#7fac55', opacity: 0.28, pulse: 0 }
  if (id === 'cave') return { color: '#7a6aa0', opacity: 0.24, pulse: 0 }
  return { color: '#8a6a3d', opacity: 0.26, pulse: 0 }
}

function tileSurfaceDetailKindForBiome(id: BiomeTheme['id']): TileSurfaceDetailKind {
  if (id === 'forest') return 'forest-moss-root'
  if (id === 'cave') return 'cave-slate-chip'
  if (id === 'lava') return 'lava-basalt-fissure'
  if (id === 'ice') return 'ice-snow-crust'
  if (id === 'magical') return 'magical-rune-inlay'
  return 'dungeon-paver-chip'
}

function tileEdgeReliefKindForBiome(id: BiomeTheme['id']): TileEdgeReliefKind {
  if (id === 'forest') return 'forest-moss-lip'
  if (id === 'cave') return 'cave-chipped-lip'
  if (id === 'lava') return 'lava-basalt-lip'
  if (id === 'ice') return 'ice-snow-lip'
  if (id === 'magical') return 'magical-rune-lip'
  return 'dungeon-paver-lip'
}

export function tileSurfaceForBiome(tile: string, theme: BiomeTheme, x = 0, y = 0): SurfaceStyle {
  const tone = (hashCell(`${theme.id}:${tile}:${x}:${y}`) % 1000) / 1000
  const materialFlip = hashCell(`${theme.id}:material:${x}:${y}:${tile}`) % 5
  const vary = (tone - 0.5) * 0.085
  if (tile === 'l') return { top: '#ff6a35', body: '#210a07', accent: '#ffd45c', detail: '#ff9a45', emissive: '#ff3d16', roughness: 0.58, metalness: 0.02, glow: 1.2 }
  if (tile === '~') return { top: '#4fd5ff', body: '#0c3148', accent: '#9ff0ff', detail: '#d7fbff', emissive: '#36bfff', roughness: 0.22, metalness: 0.04, glow: 0.65 }
  if (tile === 's') return { top: '#c9f4ff', body: '#1d5a72', accent: '#ffffff', detail: '#75dfff', emissive: '#75dfff', roughness: 0.34, metalness: 0.03, glow: 0.22 }
  if (tile === 'm') return { top: '#8c4ed9', body: '#1c0b28', accent: '#d59bff', detail: '#bf76ff', emissive: '#7f38d8', roughness: 0.62, metalness: 0.02, glow: 0.72 }
  if (tile === 'd') return { top: '#d0a24c', body: '#5a3518', accent: '#ffe08a', detail: '#7a4f1e', emissive: '#ffb454', roughness: 0.58, metalness: 0.18, glow: 0.18 }
  if (tile === 'b') return { top: mixed(theme.biomeSoft, '#ffffff', 0.1), body: theme.biome, accent: '#ffffff', detail: theme.edge, emissive: theme.biome, roughness: 0.68, metalness: 0.02, glow: 0.2 }
  if (tile === 'x') {
    if (theme.id === 'forest') return { top: '#6f9252', body: '#3b2e1b', accent: '#c4e47c', detail: '#7b5639', emissive: theme.biome, roughness: 0.96, metalness: 0, glow: 0.06 }
    if (theme.id === 'cave') return { top: '#9a8ab9', body: '#3b304d', accent: '#d8ceff', detail: '#8979aa', emissive: theme.biome, roughness: 0.9, metalness: 0.01, glow: 0.16 }
    if (theme.id === 'lava') return { top: '#85675b', body: '#2d1711', accent: '#ff8a4d', detail: '#ffb15a', emissive: '#ff3d16', roughness: 0.84, metalness: 0.02, glow: 0.3 }
    if (theme.id === 'ice') return { top: '#d8fbff', body: '#2f7290', accent: '#ffffff', detail: '#6eccea', emissive: theme.biome, roughness: 0.42, metalness: 0.02, glow: 0.2 }
    if (theme.id === 'magical') return { top: '#9b5ebb', body: '#3d204d', accent: '#f2a7ff', detail: '#7de3ff', emissive: theme.biome, roughness: 0.72, metalness: 0.02, glow: 0.36 }
    return { top: '#96784b', body: '#40311f', accent: '#e4bd72', detail: '#9a7a49', emissive: '#ffb454', roughness: 0.88, metalness: 0.04, glow: 0.16 }
  }
  if (theme.id === 'forest') {
    const earthy = materialFlip === 0
    return earthy
      ? { top: shifted(mixed('#bda36f', '#e1d097', tone * 0.5), vary), body: '#4e3822', accent: '#e8d390', detail: '#76522e', emissive: theme.biome, roughness: 0.96, metalness: 0, glow: 0.04 }
      : { top: shifted(mixed('#7ead60', '#c8dc82', tone * 0.5), vary), body: '#352a17', accent: '#c9ea82', detail: '#6d4a2d', emissive: theme.biome, roughness: 0.98, metalness: 0, glow: 0.05 }
  }
  if (theme.id === 'cave') return { top: shifted(mixed('#a996bb', '#d7cbed', tone * 0.46), vary), body: '#403553', accent: '#ded3ff', detail: '#746585', emissive: theme.biome, roughness: 0.94, metalness: 0.01, glow: 0.16 }
  if (theme.id === 'lava') return { top: shifted(mixed('#746b65', '#aa9c91', tone * 0.36), vary), body: '#1f0e09', accent: '#ff8b4f', detail: '#ffc166', emissive: '#ff3d16', roughness: 0.9, metalness: 0.01, glow: 0.52 }
  if (theme.id === 'ice') return { top: shifted(mixed('#d9f8ff', '#ffffff', tone * 0.55), vary), body: '#2a6a87', accent: '#ffffff', detail: '#92e8ff', emissive: theme.biome, roughness: 0.42, metalness: 0.01, glow: 0.24 }
  if (theme.id === 'magical') return { top: shifted(mixed('#9b6ba8', '#d994e5', tone * 0.46), vary), body: '#392147', accent: '#f9b8ff', detail: '#9af0ff', emissive: theme.biome, roughness: 0.78, metalness: 0.01, glow: 0.56 }
  return { top: shifted(mixed('#9b8159', '#d1b06f', tone * 0.44), vary), body: '#44331f', accent: '#f0ca83', detail: '#604729', emissive: '#ffb454', roughness: 0.92, metalness: 0.02, glow: 0.22 }
}

function surfaceTexture(id: string): THREE.Texture | null {
  if (surfaceTextureCache.has(id)) return surfaceTextureCache.get(id)!
  if (typeof document === 'undefined') return null
  const canvas = document.createElement('canvas')
  canvas.width = 128
  canvas.height = 128
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.fillStyle = '#eeeeee'
  ctx.fillRect(0, 0, 128, 128)
  const seed = hashCell(id)
  let s = seed
  const rnd = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 0xffffffff)
  const crackTone = id === 'lava' ? 72 : id === 'ice' ? 170 : id === 'forest' ? 96 : id === 'dungeon' ? 86 : 70
  for (let i = 0; i < 220; i++) {
    const v = 196 + Math.floor(rnd() * 48)
    ctx.fillStyle = `rgba(${v},${v},${v},${0.08 + rnd() * 0.12})`
    ctx.beginPath()
    ctx.arc(rnd() * 128, rnd() * 128, 0.7 + rnd() * 2.4, 0, Math.PI * 2)
    ctx.fill()
  }
  for (let i = 0; i < 13; i++) {
    ctx.strokeStyle = `rgba(${crackTone},${crackTone},${crackTone},${0.12 + rnd() * 0.13})`
    ctx.lineWidth = 0.8 + rnd() * 1.5
    ctx.beginPath()
    const x = rnd() * 128
    const y = rnd() * 128
    ctx.moveTo(x, y)
    ctx.lineTo(x + (rnd() - 0.5) * 74, y + (rnd() - 0.5) * 74)
    ctx.stroke()
  }
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.repeat.set(1.8, 1.8)
  surfaceTextureCache.set(id, tex)
  return tex
}

export function buildTileSurfaceDetails(cells: string[], theme: BiomeTheme, graphics: 'high' | 'medium' | 'low'): TileSurfaceDetailInfo[] {
  if (graphics === 'low') return []
  const kind = tileSurfaceDetailKindForBiome(theme.id)
  const details: TileSurfaceDetailInfo[] = []
  cells.forEach((row, y) => {
    row.split('').forEach((tile, x) => {
      if (!FLOOR_TILES.includes(tile)) return
      const seed = hashCell(`${theme.id}:surface-detail:${x}:${y}:${tile}`)
      if (seed % 4 === 0) return
      if (graphics === 'medium' && seed % 2 !== 0) return
      const tone = ((seed >>> 5) % 1000) / 1000
      details.push({
        key: `${x}-${y}-${tile}-surface-detail`,
        x,
        y,
        tile,
        kind,
        rot: (((seed >>> 12) % 32) / 32) * Math.PI * 2,
        scale: 0.88 + (((seed >>> 18) % 14) / 100),
        tone,
        wear: 0.34 + (((seed >>> 8) % 22) / 100),
        edgeWear: ((seed >>> 26) % 16) / 15,
        patchCount: graphics === 'high' ? 1 + ((seed >>> 28) % 3) : 1 + ((seed >>> 28) % 2),
        patchSkew: ((seed >>> 16) % 100) / 100,
        variant: (seed >>> 22) % 11,
      })
    })
  })
  return details
}

function cellAt(cells: string[], x: number, y: number): string {
  if (y < 0 || y >= cells.length) return ' '
  return cells[y][x] ?? ' '
}

function isOrdinaryFloorCell(cells: string[], x: number, y: number): boolean {
  return FLOOR_TILES.includes(cellAt(cells, x, y))
}

export function buildTileEdgeRelief(cells: string[], theme: BiomeTheme, graphics: 'high' | 'medium' | 'low'): TileEdgeReliefInfo[] {
  if (graphics === 'low') return []
  const kind = tileEdgeReliefKindForBiome(theme.id)
  const sides: { side: TileEdgeReliefSide; dx: number; dy: number }[] = [
    { side: 'north', dx: 0, dy: -1 },
    { side: 'south', dx: 0, dy: 1 },
    { side: 'west', dx: -1, dy: 0 },
    { side: 'east', dx: 1, dy: 0 },
  ]
  const pieces: TileEdgeReliefInfo[] = []
  let playableCount = 0
  const lastRow = cells.length - 1
  cells.forEach((row, y) => {
    row.split('').forEach((tile, x) => {
      if (!FLOOR_TILES.includes(tile)) return
      playableCount += 1
      let interiorAdded = false
      sides.forEach(({ side, dx, dy }) => {
        const boundary = !isOrdinaryFloorCell(cells, x + dx, y + dy)
        const seed = hashCell(`${theme.id}:edge-relief:${x}:${y}:${tile}:${side}`)
        const frontEdge = side === 'south' && y >= lastRow - 1
        const keepBoundary = boundary && (frontEdge ? seed % 3 === 0 : true)
        const keepInterior = !boundary && !interiorAdded && seed % (graphics === 'high' ? 4 : 10) === 0
        if (!keepBoundary && !keepInterior) return
        if (graphics === 'medium') {
          if (frontEdge) return
          if (boundary ? seed % 3 === 1 : seed % 2 !== 0) return
        }
        if (!boundary) interiorAdded = true
        const tone = ((seed >>> 6) % 1000) / 1000
        pieces.push({
          key: `${x}-${y}-${tile}-${side}-edge-relief`,
          x,
          y,
          tile,
          side,
          kind,
          boundary,
          rot: ((((seed >>> 13) % 100) / 100) - 0.5) * 0.24,
          offset: boundary ? 0.42 + (((seed >>> 17) % 9) / 100) : 0.34 + (((seed >>> 17) % 7) / 100),
          shift: ((((seed >>> 21) % 100) / 100) - 0.5) * 0.32,
          length: (boundary ? 0.5 : 0.4) + (((seed >>> 8) % 18) / 100),
          depth: (boundary ? 0.092 : 0.064) + (((seed >>> 24) % 9) / 1000),
          height: (boundary ? 0.018 : 0.012) + (((seed >>> 27) % 8) / 1000),
          tone,
          opacity: boundary ? 0.66 + tone * 0.2 : 0.5 + tone * 0.14,
          variant: (seed >>> 28) % 13,
        })
      })
    })
  })
  const cap = graphics === 'high' ? Math.min(88, Math.ceil(playableCount * 1.62)) : Math.min(42, Math.ceil(playableCount * 0.72))
  return pieces.slice(0, cap)
}

export function Tiles({ cells, layout, theme, reducedMotion, graphics }: TilesProps) {
  const floorGeom = useMemo(() => makeRoundedBox(layout.tileSize * FLOOR_SLAB_SCALE, FLOOR_H, layout.tileSize * FLOOR_SLAB_SCALE, 0.09, 3), [layout.tileSize])
  const capGeom = useMemo(() => makeRoundedBox(layout.tileSize * CAP_SLAB_SCALE, CAP_H, layout.tileSize * CAP_SLAB_SCALE, 0.1, 3), [layout.tileSize])
  const wallGeom = useMemo(() => makeRoundedBox(layout.tileSize * WALL_SLAB_SCALE, WALL_H, layout.tileSize * WALL_SLAB_SCALE, 0.09, 3), [layout.tileSize])
  const surfaceMap = useMemo(() => graphics === 'low' ? null : surfaceTexture(theme.id), [graphics, theme.id])

  const { floors, specials, lowWalls } = useMemo(() => {
    const floors: { key: string; x: number; y: number; tile: string }[] = []
    const specials: { key: string; x: number; y: number; tile: string }[] = []
    const lowWalls: { key: string; x: number; y: number; tile: string }[] = []
    cells.forEach((row, y) => {
      row.split('').forEach((tile, x) => {
        if (graphics === 'low') {
          if (LOW_FLOOR_TILES.includes(tile)) floors.push({ key: `${x}-${y}-${tile}`, x, y, tile })
          else if (WALL_TILES.includes(tile)) lowWalls.push({ key: `${x}-${y}-${tile}`, x, y, tile })
        } else if (FLOOR_TILES.includes(tile)) floors.push({ key: `${x}-${y}-${tile}`, x, y, tile })
        else if ('xbdsl~wm'.includes(tile)) specials.push({ key: `${x}-${y}-${tile}`, x, y, tile })
      })
    })
    return { floors, specials, lowWalls }
  }, [cells, graphics])
  const seamSegments = useMemo(() => {
    const style = graphics === 'low' ? null : biomeDeckSeamStyle(theme.id)
    if (!style) return []
    const floorKeys = new Set(floors.map((f) => `${f.x}:${f.y}`))
    const segments: DeckSeamSegment[] = []
    const pitch = layout.tileSize + layout.gap
    const seamWidth = theme.id === 'ice' ? 0.062 : 0.078
    const seamLength = layout.tileSize * (theme.id === 'ice' ? 0.98 : 0.99)
    const seamHeight = 0.014
    floors.forEach((f) => {
      const [wx, , wz] = layout.toWorld(f.x, f.y)
      if (floorKeys.has(`${f.x + 1}:${f.y}`)) {
        segments.push({ key: `${f.x}-${f.y}-east`, x: wx + pitch / 2, y: wz, scale: [seamWidth, seamHeight, seamLength] })
      }
      if (floorKeys.has(`${f.x}:${f.y + 1}`)) {
        segments.push({ key: `${f.x}-${f.y}-south`, x: wx, y: wz + pitch / 2, scale: [seamLength, seamHeight, seamWidth] })
      }
    })
    if (graphics === 'medium') return segments.filter((_, i) => i % 2 === 0)
    return segments
  }, [floors, graphics, layout, theme.id])

  const bodyRef = useRef<THREE.InstancedMesh>(null)
  const capRef = useRef<THREE.InstancedMesh>(null)
  const wallBodyRef = useRef<THREE.InstancedMesh>(null)
  const wallCapRef = useRef<THREE.InstancedMesh>(null)
  const detailTiles = useMemo(() => buildTileSurfaceDetails(cells, theme, graphics), [cells, graphics, theme])
  const edgeReliefTiles = useMemo(() => buildTileEdgeRelief(cells, theme, graphics), [cells, graphics, theme])

  useLayoutEffect(() => {
    const body = bodyRef.current
    const cap = capRef.current
    if (!body || !cap || typeof body.setMatrixAt !== 'function' || typeof cap.setMatrixAt !== 'function') return
    const m = new THREE.Matrix4()
    const q = new THREE.Quaternion()
    const color = new THREE.Color()
    floors.forEach((f, i) => {
      const [wx, , wz] = layout.toWorld(f.x, f.y)
      const seed = hashCell(`${theme.id}:${f.tile}:${f.x}:${f.y}`)
      const rot = ((seed % 7) - 3) * 0.018
      const sx = 0.985 + ((seed >> 3) % 5) * 0.004
      const sz = 0.985 + ((seed >> 7) % 5) * 0.004
      const surface = tileSurfaceForBiome(f.tile, theme, f.x, f.y)
      q.setFromEuler(new THREE.Euler(0, rot, 0))
      m.compose(new THREE.Vector3(wx, FLOOR_H / 2 - 0.005, wz), q, new THREE.Vector3(sx, 1, sz))
      body.setMatrixAt(i, m)
      body.setColorAt(i, color.set(surface.body))
      m.compose(new THREE.Vector3(wx, FLOOR_H + CAP_H / 2, wz), q, new THREE.Vector3(sx * 0.99, 1, sz * 0.99))
      cap.setMatrixAt(i, m)
      cap.setColorAt(i, color.set(surface.top))
    })
    body.count = floors.length
    cap.count = floors.length
    body.instanceMatrix.needsUpdate = true
    cap.instanceMatrix.needsUpdate = true
    if (body.instanceColor) body.instanceColor.needsUpdate = true
    if (cap.instanceColor) cap.instanceColor.needsUpdate = true
  }, [floors, layout, theme])

  useLayoutEffect(() => {
    const body = wallBodyRef.current
    const cap = wallCapRef.current
    if (!body || !cap || typeof body.setMatrixAt !== 'function' || typeof cap.setMatrixAt !== 'function') return
    const m = new THREE.Matrix4()
    const q = new THREE.Quaternion()
    const color = new THREE.Color()
    lowWalls.forEach((f, i) => {
      const [wx, , wz] = layout.toWorld(f.x, f.y)
      const seed = hashCell(`${theme.id}:wall:${f.tile}:${f.x}:${f.y}`)
      const rot = ((seed % 7) - 3) * 0.018
      const sx = 0.985 + ((seed >> 3) % 5) * 0.004
      const sz = 0.985 + ((seed >> 7) % 5) * 0.004
      const surface = tileSurfaceForBiome(f.tile, theme, f.x, f.y)
      q.setFromEuler(new THREE.Euler(0, rot, 0))
      m.compose(new THREE.Vector3(wx, WALL_H / 2 - 0.005, wz), q, new THREE.Vector3(sx, 1, sz))
      body.setMatrixAt(i, m)
      body.setColorAt(i, color.set(surface.body))
      m.compose(new THREE.Vector3(wx, WALL_H + CAP_H / 2, wz), q, new THREE.Vector3(sx * 0.99, 1, sz * 0.99))
      cap.setMatrixAt(i, m)
      cap.setColorAt(i, color.set(surface.top))
    })
    body.count = lowWalls.length
    cap.count = lowWalls.length
    body.instanceMatrix.needsUpdate = true
    cap.instanceMatrix.needsUpdate = true
    if (body.instanceColor) body.instanceColor.needsUpdate = true
    if (cap.instanceColor) cap.instanceColor.needsUpdate = true
  }, [lowWalls, layout, theme])

  const baseSurface = tileSurfaceForBiome('-', theme)
  const bodyEmissiveIntensity = theme.id === 'lava' ? 0.04 : Math.max(0.03, baseSurface.glow * 0.16)
  const capEmissiveIntensity = theme.id === 'lava' ? 0.08 : Math.max(0.08, baseSurface.glow * 0.5)

  return (
    <group>
      <instancedMesh ref={bodyRef} args={[floorGeom, undefined, Math.max(1, floors.length)]} castShadow receiveShadow>
        {graphics === 'low'
          ? <meshBasicMaterial vertexColors />
          : <meshStandardMaterial vertexColors roughness={baseSurface.roughness} metalness={baseSurface.metalness} emissive={baseSurface.emissive} emissiveIntensity={bodyEmissiveIntensity} />}
      </instancedMesh>
      <instancedMesh ref={capRef} args={[capGeom, undefined, Math.max(1, floors.length)]} castShadow receiveShadow>
        {graphics === 'low'
          ? <meshBasicMaterial vertexColors />
          : <meshStandardMaterial vertexColors roughness={baseSurface.roughness} metalness={baseSurface.metalness} map={surfaceMap ?? undefined} bumpMap={surfaceMap ?? undefined} bumpScale={0.018} emissive={baseSurface.emissive} emissiveIntensity={capEmissiveIntensity} />}
      </instancedMesh>
      {graphics === 'low' && lowWalls.length > 0 && (
        <>
          <instancedMesh ref={wallBodyRef} args={[wallGeom, undefined, lowWalls.length]} castShadow receiveShadow>
            <meshBasicMaterial vertexColors />
          </instancedMesh>
          <instancedMesh ref={wallCapRef} args={[capGeom, undefined, lowWalls.length]} castShadow receiveShadow>
            <meshBasicMaterial vertexColors />
          </instancedMesh>
        </>
      )}

      {detailTiles.map((t) => (
        <TileDetail key={t.key} info={t} layout={layout} theme={theme} reducedMotion={reducedMotion} graphics={graphics} />
      ))}

      {edgeReliefTiles.map((t) => (
        <TileEdgeRelief key={t.key} info={t} layout={layout} theme={theme} graphics={graphics} />
      ))}

      {seamSegments.length > 0 && <DeckSeams segments={seamSegments} theme={theme} reducedMotion={reducedMotion} />}

      {specials.map((t) => (
        <SpecialTile key={t.key} info={t} layout={layout} theme={theme} reducedMotion={reducedMotion} graphics={graphics} wallGeom={wallGeom} capGeom={capGeom} floorGeom={floorGeom} surfaceMap={surfaceMap} />
      ))}
    </group>
  )
}

function DeckSeams({ segments, theme, reducedMotion }: { segments: DeckSeamSegment[]; theme: BiomeTheme; reducedMotion: boolean }) {
  const meshRef = useRef<THREE.InstancedMesh>(null)
  const materialRef = useRef<THREE.MeshBasicMaterial>(null)
  const seamGeom = useMemo(() => new THREE.BoxGeometry(1, 1, 1), [])
  const style = biomeDeckSeamStyle(theme.id)

  useLayoutEffect(() => {
    const mesh = meshRef.current
    if (!mesh || typeof mesh.setMatrixAt !== 'function') return
    const matrix = new THREE.Matrix4()
    const quaternion = new THREE.Quaternion()
    segments.forEach((segment, i) => {
      matrix.compose(new THREE.Vector3(segment.x, FLOOR_H + CAP_H + 0.016, segment.y), quaternion, new THREE.Vector3(segment.scale[0], segment.scale[1], segment.scale[2]))
      mesh.setMatrixAt(i, matrix)
    })
    mesh.count = segments.length
    mesh.instanceMatrix.needsUpdate = true
  }, [segments])

  useFrame((state) => {
    if (!materialRef.current || !style || reducedMotion) return
    materialRef.current.opacity = style.opacity + Math.sin(state.clock.elapsedTime * 2.8) * style.pulse
  })

  if (!style) return null
  return (
    <instancedMesh ref={meshRef} args={[seamGeom, undefined, Math.max(1, segments.length)]} renderOrder={2}>
      <meshBasicMaterial ref={materialRef} color={style.color} transparent opacity={style.opacity} toneMapped={false} depthWrite={false} />
    </instancedMesh>
  )
}

function GlowLine({ color, position, rotation, scale, reducedMotion }: { color: string; position: [number, number, number]; rotation?: [number, number, number]; scale?: [number, number, number]; reducedMotion: boolean }) {
  const ref = useRef<THREE.MeshBasicMaterial>(null)
  useFrame((state) => {
    if (!ref.current || reducedMotion) return
    ref.current.opacity = 0.45 + Math.sin(state.clock.elapsedTime * 3.4 + position[0] * 3 + position[2] * 2) * 0.22
  })
  return (
    <mesh position={position} rotation={rotation} scale={scale}>
      <boxGeometry args={[0.44, 0.018, 0.04]} />
      <meshBasicMaterial ref={ref} color={color} transparent opacity={0.7} toneMapped={false} />
    </mesh>
  )
}

function patchNoise(seed: number, i: number): number {
  return Math.sin((seed + 1) * (i + 3) * 12.9898) * 0.5 + 0.5
}

function FlatPatch({
  color,
  position,
  rotation,
  radius,
  stretch,
  seed,
  opacity = 1,
  roughness = 0.96,
  emissive,
  emissiveIntensity = 0,
  basic = false,
}: {
  color: string
  position: [number, number, number]
  rotation: number
  radius: number
  stretch: [number, number]
  seed: number
  opacity?: number
  roughness?: number
  emissive?: string
  emissiveIntensity?: number
  basic?: boolean
}) {
  const shape = useMemo(() => {
    const s = new THREE.Shape()
    const sides = 8 + (seed % 5)
    for (let i = 0; i < sides; i++) {
      const a = (i / sides) * Math.PI * 2 + (patchNoise(seed, i) - 0.5) * 0.18
      const r = radius * (0.78 + patchNoise(seed + 17, i) * 0.28)
      const x = Math.cos(a) * r * stretch[0]
      const y = Math.sin(a) * r * stretch[1]
      if (i === 0) s.moveTo(x, y)
      else s.lineTo(x, y)
    }
    s.closePath()
    return s
  }, [radius, seed, stretch])

  return (
    <mesh position={position} rotation={[Math.PI / 2, 0, rotation]}>
      <shapeGeometry args={[shape]} />
      {basic
        ? <meshBasicMaterial color={color} transparent opacity={opacity} toneMapped={false} side={THREE.DoubleSide} depthWrite={false} />
        : <meshStandardMaterial color={color} roughness={roughness} transparent opacity={opacity} emissive={emissive ?? '#000000'} emissiveIntensity={emissiveIntensity} side={THREE.DoubleSide} />}
    </mesh>
  )
}

function TileEdgeRelief({ info, layout, theme, graphics }: { info: TileEdgeReliefInfo; layout: BoardLayout; theme: BiomeTheme; graphics: 'high' | 'medium' | 'low' }) {
  const [wx, , wz] = layout.toWorld(info.x, info.y)
  const surface = tileSurfaceForBiome(info.tile, theme, info.x, info.y)
  const horizontal = info.side === 'north' || info.side === 'south'
  const dir = info.side === 'north' || info.side === 'west' ? -1 : 1
  const sideOffset = info.offset * layout.tileSize * dir
  const shift = info.shift * layout.tileSize
  const px = horizontal ? shift : sideOffset
  const pz = horizontal ? sideOffset : shift
  const rot = (horizontal ? 0 : Math.PI / 2) + info.rot
  const top = FLOOR_H + CAP_H + 0.022
  const seed = hashCell(`${info.key}:shape`)
  const mutedTop = mixed(surface.top, surface.detail, 0.2 + info.tone * 0.18)
  const darkEdge = mixed(surface.body, '#080605', 0.18)
  const lightEdge = mixed(surface.top, surface.accent, 0.2 + info.tone * 0.16)
  const stripHeight = Math.max(0.01, info.height)

  if (info.kind === 'forest-moss-lip') {
    return (
      <group position={[wx, top, wz]}>
        <FlatPatch
          color={info.variant % 3 === 0 ? mixed('#2f5734', surface.top, 0.18) : mixed('#77b957', surface.top, 0.2)}
          position={[px, stripHeight, pz]}
          rotation={rot}
          radius={0.15 + info.depth * 0.32}
          stretch={[1.26 + info.length * 1.2, 0.38 + info.depth * 1.9]}
          seed={seed}
          opacity={Math.min(0.86, info.opacity + 0.08)}
          roughness={0.99}
        />
        {graphics === 'high' && (info.boundary || info.variant % 3 === 0) && (
          <mesh position={[px - Math.sin(rot) * 0.035, stripHeight + 0.012, pz + Math.cos(rot) * 0.035]} rotation={[Math.PI / 2, 0, rot + 0.18]}>
            <capsuleGeometry args={[0.012, info.length * 0.72, 4, 7]} />
            <meshStandardMaterial color={mixed('#6d4a2d', surface.detail, 0.18)} roughness={0.98} />
          </mesh>
        )}
      </group>
    )
  }

  if (info.kind === 'cave-chipped-lip') {
    return (
      <group position={[wx, top, wz]}>
        <FlatPatch
          color={info.variant % 2 === 0 ? mixed(surface.top, '#4a3f61', 0.36) : mixed(surface.top, surface.accent, 0.16)}
          position={[px, stripHeight, pz]}
          rotation={rot}
          radius={0.13 + info.depth * 0.28}
          stretch={[1.2 + info.length * 1.12, 0.34 + info.depth * 1.6]}
          seed={seed}
          opacity={Math.min(0.76, info.opacity + 0.04)}
          roughness={0.98}
        />
        {graphics === 'high' && info.boundary && info.variant % 2 === 0 && (
          <mesh position={[px + Math.cos(rot) * info.length * 0.18, stripHeight + 0.034, pz + Math.sin(rot) * info.length * 0.18]} rotation={[0.08, info.tone * Math.PI, 0.12]} castShadow>
            <octahedronGeometry args={[0.034, 0]} />
            <meshStandardMaterial color={mixed(surface.top, surface.detail, 0.45)} roughness={0.9} flatShading />
          </mesh>
        )}
      </group>
    )
  }

  if (info.kind === 'lava-basalt-lip') {
    return (
      <group position={[wx, top, wz]}>
        <mesh position={[px, stripHeight, pz]} rotation={[0, rot, 0]} castShadow receiveShadow>
          <boxGeometry args={[info.length, stripHeight, info.depth]} />
          <meshStandardMaterial color={darkEdge} roughness={0.98} emissive={surface.emissive} emissiveIntensity={0.08} />
        </mesh>
        {(info.boundary || info.variant % 3 === 0) && (
          <mesh position={[px, stripHeight + 0.012, pz]} rotation={[0, rot, 0]}>
            <boxGeometry args={[info.length * 0.72, 0.008, info.depth * 0.28]} />
            <meshBasicMaterial color={info.variant % 2 === 0 ? surface.emissive : surface.detail} transparent opacity={0.58 + info.tone * 0.18} toneMapped={false} depthWrite={false} />
          </mesh>
        )}
      </group>
    )
  }

  if (info.kind === 'ice-snow-lip') {
    return (
      <group position={[wx, top, wz]}>
        <FlatPatch
          color={info.variant % 2 === 0 ? '#f4fbff' : mixed('#ffffff', surface.top, 0.46)}
          position={[px, stripHeight, pz]}
          rotation={rot}
          radius={0.13 + info.depth * 0.24}
          stretch={[1.16 + info.length * 1.08, 0.34 + info.depth * 1.6]}
          seed={seed}
          opacity={0.52 + info.tone * 0.12}
          roughness={0.5}
          emissive={surface.emissive}
          emissiveIntensity={0.025}
        />
        {graphics === 'high' && info.boundary && info.variant % 3 === 0 && (
          <mesh position={[px, stripHeight + 0.06, pz]} rotation={[0.16, info.tone * Math.PI, -0.08]} castShadow>
            <coneGeometry args={[0.026, 0.105, 5]} />
            <meshStandardMaterial color={surface.accent} emissive={surface.emissive} emissiveIntensity={0.12} roughness={0.26} transparent opacity={0.8} />
          </mesh>
        )}
      </group>
    )
  }

  if (info.kind === 'magical-rune-lip') {
    return (
      <group position={[wx, top + 0.008, wz]}>
        <FlatPatch
          color={info.variant % 2 === 0 ? mixed(surface.top, surface.accent, 0.32) : surface.detail}
          position={[px, stripHeight, pz]}
          rotation={rot}
          radius={0.12 + info.depth * 0.24}
          stretch={[1.1 + info.length * 1.08, 0.32 + info.depth * 1.5]}
          seed={seed}
          opacity={0.34 + info.tone * 0.14}
          basic
        />
        {(info.boundary || info.variant % 4 === 0) && (
          <mesh position={[px, stripHeight + 0.008, pz]} rotation={[0, rot, 0]}>
            <boxGeometry args={[info.length * 0.58, 0.006, info.depth * 0.22]} />
            <meshBasicMaterial color={surface.accent} transparent opacity={0.56} toneMapped={false} depthWrite={false} />
          </mesh>
        )}
      </group>
    )
  }

  if (info.kind === 'dungeon-paver-lip') {
    return (
      <group position={[wx, top, wz]}>
        <mesh position={[px, stripHeight, pz]} rotation={[0, rot, 0]} castShadow receiveShadow>
          <boxGeometry args={[info.length, stripHeight, info.depth]} />
          <meshStandardMaterial color={mutedTop} roughness={0.98} metalness={0.02} />
        </mesh>
        {graphics === 'high' && info.variant % 3 === 0 && (
          <mesh position={[px + Math.cos(rot) * info.length * 0.18, stripHeight + 0.014, pz + Math.sin(rot) * info.length * 0.18]} rotation={[0.1, info.tone * Math.PI, 0.1]} castShadow>
            <octahedronGeometry args={[0.03, 0]} />
            <meshStandardMaterial color={mixed(surface.top, surface.detail, 0.4)} roughness={0.92} />
          </mesh>
        )}
      </group>
    )
  }

  return (
    <group position={[wx, top, wz]}>
      <FlatPatch
        color={mutedTop}
        position={[px, stripHeight, pz]}
        rotation={rot}
        radius={0.125 + info.depth * 0.22}
        stretch={[1.12 + info.length * 1.06, 0.34 + info.depth * 1.45]}
        seed={seed}
        opacity={0.48 + info.tone * 0.12}
        roughness={0.97}
      />
      {graphics === 'high' && info.boundary && info.variant % 4 === 0 && (
        <mesh position={[px, stripHeight + 0.05, pz]} rotation={[0.12, info.tone * Math.PI, 0.08]} castShadow>
          <octahedronGeometry args={[0.036, 0]} />
          <meshStandardMaterial color={lightEdge} emissive={surface.emissive} emissiveIntensity={0.12} roughness={0.42} transparent opacity={0.82} />
        </mesh>
      )}
    </group>
  )
}

function TileDetail({ info, layout, theme, reducedMotion, graphics }: { info: TileSurfaceDetailInfo; layout: BoardLayout; theme: BiomeTheme; reducedMotion: boolean; graphics: 'high' | 'medium' | 'low' }) {
  const [wx, , wz] = layout.toWorld(info.x, info.y)
  const surface = tileSurfaceForBiome(info.tile, theme, info.x, info.y)
  const top = FLOOR_H + CAP_H + 0.016
  const chipB = mixed(surface.top, surface.accent, 0.18 + info.tone * 0.2)
  const edgeSign = info.variant % 2 === 0 ? 1 : -1
  const crossSign = info.variant % 3 === 0 ? 1 : -1
  const broadX = edgeSign * (0.08 + info.edgeWear * 0.12)
  const broadZ = crossSign * (0.06 + info.wear * 0.16)
  const edgeX = edgeSign * (0.3 + info.edgeWear * 0.06)
  const edgeZ = crossSign * (0.16 + info.wear * 0.12)
  const patchSeeds = useMemo(
    () => Array.from({ length: info.patchCount }, (_, i) => hashCell(`${theme.id}:patch:${info.x}:${info.y}:${info.variant}:${i}`)),
    [info.patchCount, info.variant, info.x, info.y, theme.id],
  )
  if (info.kind === 'forest-moss-root') {
    return (
      <group position={[wx, top, wz]} rotation={[0, info.rot, 0]} scale={info.scale}>
        {patchSeeds.map((seed, i) => (
          <FlatPatch
            key={seed}
            color={i % 2 === 0 ? chipB : mixed(surface.top, '#2f5740', 0.38)}
            position={[broadX * (i % 2 === 0 ? 1 : -0.78) + (i - 1) * 0.045, 0.014 + i * 0.001, broadZ * (i % 2 === 0 ? 1 : -0.8)]}
            rotation={(i - 1) * 0.38 + info.patchSkew * 0.6}
            radius={0.13 + info.wear * 0.055}
            stretch={[1.32 + info.edgeWear * 0.28, 0.78 + info.wear * 0.2]}
            seed={seed}
            opacity={0.66}
            roughness={0.98}
          />
        ))}
        <mesh position={[edgeSign * 0.08, 0.02, crossSign * 0.02]} rotation={[Math.PI / 2, 0, 0.38 + info.edgeWear * 0.5]}>
          <capsuleGeometry args={[0.014, 0.46 + info.wear * 0.32, 4, 8]} />
          <meshStandardMaterial color={surface.detail} roughness={0.96} />
        </mesh>
        <mesh position={[edgeX * -0.55, 0.016, edgeZ]} rotation={[0, -0.28 + info.edgeWear * 0.44, 0]}>
          <boxGeometry args={[0.28 + info.wear * 0.28, 0.012, 0.04 + info.edgeWear * 0.022]} />
          <meshStandardMaterial color={chipB} roughness={0.97} />
        </mesh>
        {info.variant % 2 === 0 && (
          <mesh position={[edgeX, 0.018, edgeZ * 0.74]} rotation={[0, 0.72, 0]}>
            <boxGeometry args={[0.18 + info.edgeWear * 0.14, 0.014, 0.052]} />
            <meshStandardMaterial color={mixed('#7b5639', surface.top, 0.24)} roughness={0.98} />
          </mesh>
        )}
      </group>
    )
  }
  if (info.kind === 'cave-slate-chip') {
    const slateA = mixed(surface.top, surface.detail, 0.24 + info.tone * 0.1)
    const slateB = mixed(surface.top, surface.accent, 0.08 + info.tone * 0.08)
    return (
      <group position={[wx, top, wz]} rotation={[0, info.rot, 0]} scale={info.scale}>
        {patchSeeds.map((seed, i) => (
          <FlatPatch
            key={seed}
            color={i % 3 === 0 ? mixed(surface.top, '#4a3f61', 0.28) : i % 3 === 1 ? slateA : slateB}
            position={[(i - 1.25) * 0.11 + broadX * 0.2, 0.012 + i * 0.001, (i % 2 === 0 ? broadZ : -edgeZ) * 0.55]}
            rotation={-0.36 + i * 0.38 + info.patchSkew * 0.28}
            radius={0.105 + info.wear * 0.05}
            stretch={[1.25 + info.edgeWear * 0.28, 0.68 + info.wear * 0.18]}
            seed={seed}
            opacity={0.56}
            roughness={0.97}
          />
        ))}
        <mesh position={[-edgeX, 0.025, -0.08]} rotation={[0.1, info.tone * Math.PI, 0.14]}>
          <octahedronGeometry args={[0.045, 0]} />
          <meshStandardMaterial color={mixed(surface.top, surface.detail, 0.4)} roughness={0.88} />
        </mesh>
        {info.variant % 3 === 0 && (
          <mesh position={[0.18, 0.08, -0.12]} rotation={[0.1, 0, 0.18]}>
            <coneGeometry args={[0.038, 0.15, 5]} />
            <meshStandardMaterial color={surface.accent} emissive={surface.emissive} emissiveIntensity={0.32} roughness={0.28} transparent opacity={0.9} />
          </mesh>
        )}
      </group>
    )
  }
  if (info.kind === 'lava-basalt-fissure') {
    return (
      <group position={[wx, top, wz]} rotation={[0, info.rot, 0]} scale={info.scale}>
        {patchSeeds.slice(0, Math.max(2, info.patchCount - 1)).map((seed, i) => (
          <FlatPatch
            key={seed}
            color={i % 2 === 0 ? mixed(surface.top, '#160908', 0.42) : mixed(surface.top, '#2b1712', 0.5)}
            position={[(i - 0.8) * 0.12 + broadX * 0.18, 0.012 + i * 0.001, (i % 2 === 0 ? broadZ : -edgeZ) * 0.6]}
            rotation={-0.48 + i * 0.52 + info.patchSkew * 0.36}
            radius={0.1 + info.wear * 0.055}
            stretch={[1.34 + info.edgeWear * 0.3, 0.64 + info.wear * 0.18]}
            seed={seed}
            opacity={0.74}
            roughness={0.99}
          />
        ))}
        <GlowLine color={surface.detail} position={[0.02, 0.022, 0]} rotation={[0, 0.08, 0]} scale={[info.variant % 2 === 0 ? 1 + info.wear * 0.42 : 0.76 + info.wear * 0.26, 1, 0.95 + info.edgeWear * 0.25]} reducedMotion={reducedMotion} />
        {info.variant % 3 === 0 && <GlowLine color={surface.emissive} position={[-0.18, 0.024, 0.12]} rotation={[0, -0.72, 0]} scale={[0.48 + info.edgeWear * 0.18, 1, 0.72 + info.wear * 0.14]} reducedMotion={reducedMotion} />}
        {info.variant % 6 === 0 && <GlowLine color={surface.accent} position={[0.22, 0.026, -0.18]} rotation={[0, 0.78, 0]} scale={[0.4 + info.wear * 0.16, 1, 0.58 + info.edgeWear * 0.12]} reducedMotion={reducedMotion} />}
        {graphics === 'high' && info.variant % 5 === 0 && <pointLight color={surface.emissive} distance={1.35} intensity={0.28} position={[0, 0.16, 0]} />}
      </group>
    )
  }
  if (info.kind === 'ice-snow-crust') {
    return (
      <group position={[wx, top, wz]} rotation={[0, info.rot, 0]} scale={info.scale}>
        {patchSeeds.map((seed, i) => (
          <FlatPatch
            key={seed}
            color={i % 2 === 0 ? '#f4fbff' : mixed('#ffffff', surface.top, 0.48)}
            position={[broadX * (i % 2 === 0 ? 1 : -0.65) + (i - 1) * 0.05, 0.015 + i * 0.001, broadZ * (i % 2 === 0 ? 0.72 : -0.5)]}
            rotation={0.18 + i * 0.44 + info.patchSkew * 0.4}
            radius={0.12 + info.wear * 0.045}
            stretch={[1.28 + info.edgeWear * 0.24, 0.78 + info.wear * 0.18]}
            seed={seed}
            opacity={0.52}
            roughness={0.48}
            emissive={surface.emissive}
            emissiveIntensity={0.025}
          />
        ))}
        {info.variant % 3 === 0 && <GlowLine color={surface.detail} position={[-0.18, 0.02, 0.12]} rotation={[0, 0.45, 0]} scale={[0.58 + info.wear * 0.16, 1, 0.58]} reducedMotion={reducedMotion} />}
        {info.variant % 4 === 0 && (
          <mesh position={[0.22, 0.066, 0.12]} rotation={[0.18, 0.3, -0.12]} castShadow>
            <coneGeometry args={[0.036, 0.14, 6]} />
            <meshStandardMaterial color={surface.accent} emissive={surface.emissive} emissiveIntensity={0.14} roughness={0.3} transparent opacity={0.85} />
          </mesh>
        )}
      </group>
    )
  }
  if (info.kind === 'magical-rune-inlay') {
    return (
      <group position={[wx, top + 0.012, wz]} rotation={[0, info.rot, 0]} scale={info.scale}>
        {info.variant % 4 === 0 && (
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.16 + info.wear * 0.08, 0.009, 6, 28, Math.PI * (1.2 + info.edgeWear * 0.24)]} />
            <meshBasicMaterial color={surface.accent} transparent opacity={0.7} toneMapped={false} />
          </mesh>
        )}
        {patchSeeds.slice(0, Math.max(1, info.patchCount - 1)).map((seed, i) => (
          <FlatPatch
            key={seed}
            color={i % 2 === 0 ? mixed(surface.top, surface.accent, 0.34) : surface.detail}
            position={[(i - 0.6) * 0.12 + broadX * 0.25, 0.002 + i * 0.001, (i % 2 === 0 ? broadZ : -broadZ) * 0.5]}
            rotation={0.18 + i * 0.55 + info.patchSkew * 0.5}
            radius={0.1 + info.wear * 0.045}
            stretch={[1.25 + info.edgeWear * 0.28, 0.68 + info.wear * 0.16]}
            seed={seed}
            opacity={i % 2 === 0 ? 0.28 : 0.44}
            basic
          />
        ))}
        {info.variant % 5 === 0 && <GlowLine color={surface.detail} position={[0.12, 0.008, -0.14]} rotation={[0, 0.45, 0]} scale={[0.52 + info.wear * 0.12, 1, 0.52]} reducedMotion={reducedMotion} />}
        {info.variant % 2 === 0 && (
          <mesh position={[0.18, 0.055, 0.1]} rotation={[0.22, info.tone * Math.PI, 0.3]} castShadow>
            <octahedronGeometry args={[0.052, 0]} />
            <meshStandardMaterial color={info.variant % 4 === 0 ? '#7de3ff' : surface.accent} emissive={surface.emissive} emissiveIntensity={0.5} roughness={0.24} transparent opacity={0.9} />
          </mesh>
        )}
        {graphics === 'high' && info.variant % 6 === 0 && <pointLight color={surface.emissive} distance={1.2} intensity={0.22} position={[0, 0.2, 0]} />}
      </group>
    )
  }
  return (
    <group position={[wx, top, wz]} rotation={[0, info.rot, 0]} scale={info.scale}>
      {patchSeeds.map((seed, i) => (
        <FlatPatch
          key={seed}
          color={i % 3 === 0 ? mixed(surface.top, '#3a2a18', 0.24) : i % 3 === 1 ? mixed(surface.top, surface.detail, 0.22 + info.tone * 0.1) : mixed(surface.top, surface.accent, 0.08 + info.tone * 0.1)}
          position={[(i - 1.25) * 0.1 + broadX * 0.18, 0.012 + i * 0.001, (i % 2 === 0 ? broadZ : -edgeZ) * 0.55]}
          rotation={-0.24 + i * 0.44 + info.patchSkew * 0.32}
          radius={0.105 + info.wear * 0.05}
          stretch={[1.22 + info.edgeWear * 0.28, 0.68 + info.wear * 0.18]}
          seed={seed}
          opacity={0.56}
          roughness={0.98}
        />
      ))}
      {info.variant % 4 === 0 && (
        <mesh position={[-edgeX, 0.024, -0.08]} rotation={[0.1, info.tone * Math.PI, 0.12]}>
          <octahedronGeometry args={[0.04, 0]} />
          <meshStandardMaterial color={mixed(surface.top, surface.detail, 0.42)} roughness={0.88} />
        </mesh>
      )}
      {info.variant % 7 === 0 && (
        <group position={[0.2, 0.05, -0.12]}>
          <mesh>
            <sphereGeometry args={[0.035, 8, 6]} />
            <meshBasicMaterial color={surface.emissive} toneMapped={false} />
          </mesh>
          {graphics === 'high' && <pointLight color={surface.emissive} distance={1.4} intensity={0.35} />}
        </group>
      )}
    </group>
  )
}

function LavaSurface({ color, reducedMotion }: { color: string; reducedMotion: boolean }) {
  const group = useRef<THREE.Group>(null)
  const mat = useRef<THREE.MeshBasicMaterial>(null)
  useFrame((state) => {
    if (reducedMotion) return
    const t = state.clock.elapsedTime
    if (group.current) group.current.rotation.z = t * 0.55
    if (mat.current) mat.current.opacity = 0.72 + Math.sin(t * 4.4) * 0.18
  })
  return (
    <group ref={group} position={[0, FLOOR_H + CAP_H + 0.018, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <mesh>
        <circleGeometry args={[0.34, 32]} />
        <meshBasicMaterial ref={mat} color={color} transparent opacity={0.78} toneMapped={false} />
      </mesh>
      <mesh>
        <ringGeometry args={[0.16, 0.28, 32]} />
        <meshBasicMaterial color="#ffd45c" transparent opacity={0.45} toneMapped={false} />
      </mesh>
    </group>
  )
}

function WaterSurface({ surface, reducedMotion }: { surface: SurfaceStyle; reducedMotion: boolean }) {
  const ref = useRef<THREE.Mesh>(null)
  useFrame((state) => {
    if (!ref.current || reducedMotion) return
    const t = state.clock.elapsedTime
    ref.current.scale.setScalar(1 + Math.sin(t * 1.7) * 0.025)
  })
  return (
    <mesh ref={ref} position={[0, FLOOR_H + CAP_H + 0.018, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <circleGeometry args={[0.36, 32]} />
      <meshStandardMaterial color={surface.top} emissive={surface.emissive} emissiveIntensity={surface.glow} roughness={surface.roughness} transparent opacity={0.72} />
    </mesh>
  )
}

function SpecialTile({
  info,
  layout,
  theme,
  reducedMotion,
  graphics,
  wallGeom,
  capGeom,
  floorGeom,
  surfaceMap,
}: {
  info: { x: number; y: number; tile: string }
  layout: BoardLayout
  theme: BiomeTheme
  reducedMotion: boolean
  graphics: 'high' | 'medium' | 'low'
  wallGeom: THREE.BufferGeometry
  capGeom: THREE.BufferGeometry
  floorGeom: THREE.BufferGeometry
  surfaceMap: THREE.Texture | null
}) {
  const [wx, , wz] = layout.toWorld(info.x, info.y)
  const surface = tileSurfaceForBiome(info.tile, theme, info.x, info.y)
  const hazardRef = useRef<THREE.MeshStandardMaterial>(null)
  const isWall = info.tile === 'x' || info.tile === 'b' || info.tile === 'd'
  const bodyHeight = isWall ? WALL_H : FLOOR_H
  const bodyY = bodyHeight / 2 - 0.005
  const capY = bodyHeight + CAP_H / 2

  useFrame((state) => {
    if (reducedMotion || !hazardRef.current) return
    const t = state.clock.elapsedTime
    hazardRef.current.emissiveIntensity = surface.glow * (0.75 + Math.sin(t * 3.2 + info.x) * 0.25)
  })

  return (
    <group position={[wx, 0, wz]}>
      <mesh geometry={isWall ? wallGeom : floorGeom} position={[0, bodyY, 0]} castShadow receiveShadow>
        <meshStandardMaterial color={surface.body} roughness={surface.roughness} metalness={surface.metalness} emissive={surface.emissive} emissiveIntensity={isWall ? surface.glow * 0.28 : surface.glow * 0.12} />
      </mesh>
      <mesh geometry={capGeom} position={[0, capY, 0]} castShadow receiveShadow>
        <meshStandardMaterial color={surface.top} roughness={surface.roughness} metalness={surface.metalness} map={surfaceMap ?? undefined} bumpMap={surfaceMap ?? undefined} bumpScale={0.014} emissive={surface.emissive} emissiveIntensity={isWall ? surface.glow * 0.18 : 0} />
      </mesh>
      {info.tile === 'l' && (
        <>
          <LavaSurface color={surface.emissive} reducedMotion={reducedMotion} />
          {graphics !== 'low' && <pointLight color={surface.emissive} distance={2.2} intensity={graphics === 'high' ? 1.35 : 0.65} position={[0, 0.48, 0]} />}
        </>
      )}
      {info.tile === '~' && <WaterSurface surface={surface} reducedMotion={reducedMotion} />}
      {info.tile === 's' && (
        <mesh ref={hazardRef} position={[0, FLOOR_H + CAP_H + 0.018, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[layout.tileSize * 0.68, layout.tileSize * 0.68]} />
          <meshStandardMaterial color={surface.top} emissive={surface.emissive} emissiveIntensity={surface.glow} transparent opacity={0.58} roughness={0.22} metalness={0.02} />
        </mesh>
      )}
      {info.tile === 'm' && (
        <mesh ref={hazardRef} position={[0, FLOOR_H + CAP_H + 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.2, 0.35, 32]} />
          <meshStandardMaterial color={surface.top} emissive={surface.emissive} emissiveIntensity={surface.glow} transparent opacity={0.66} roughness={0.48} />
        </mesh>
      )}
      {info.tile === 'w' && (
        <group position={[0, bodyHeight + 0.12, 0]}>
          <mesh>
            <cylinderGeometry args={[0.18, 0.18, 0.08, 12]} />
            <meshStandardMaterial color={theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.5} roughness={0.42} />
          </mesh>
          {graphics === 'high' && <pointLight color={theme.biomeSoft} distance={1.6} intensity={0.45} />}
        </group>
      )}
      {info.tile === 'd' && (
        <group position={[0, bodyHeight + 0.08, 0]}>
          {[-1, 0, 1].map((s) => (
            <mesh key={s} position={[s * 0.13, 0.16, 0]}>
              <boxGeometry args={[0.05, 0.34, 0.06]} />
              <meshStandardMaterial color={surface.accent} roughness={0.5} metalness={0.28} />
            </mesh>
          ))}
        </group>
      )}
    </group>
  )
}
